import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  CreateInvoiceArticle,
  Invoice,
  InvoiceArticle,
  PatchInvoiceArticle
} from '@thro-types/billing';

import { AxiosRequestConfig } from 'axios';

import {
  createInvoiceArticle,
  deleteInvoiceArticle,
  updateInvoiceArticle
} from 'api/billingService';

export const useInvoiceArticleMutation = (config?: AxiosRequestConfig) => {
  const queryClient = useQueryClient();

  const queryKey = ['invoiceArticles', config ? JSON.stringify(config.params) : ''];

  const create = useMutation<Invoice[], Error, { invoiceId: string; data: CreateInvoiceArticle }>({
    mutationFn: async args => {
      return await createInvoiceArticle(args.invoiceId, args.data, config);
    },
    onSuccess: async (data, args) => {
      queryClient.invalidateQueries({ queryKey });
      // Invalidate all affected invoice queries by ID to ensure updated data is fetched
      data.map(invoice =>
        queryClient.invalidateQueries({
          queryKey: ['invoices', invoice.id]
        })
      );
    },
    onError: data => {}
  });

  const update = useMutation<
    Invoice[],
    Error,
    { invoiceId: string; id: string; data: PatchInvoiceArticle }
  >({
    mutationFn: async args => {
      return await updateInvoiceArticle(args.invoiceId, args.id, args.data, config);
    },
    onSuccess: (data, args) => {
      queryClient.invalidateQueries({ queryKey });
      data.map(invoice => queryClient.invalidateQueries({ queryKey: ['invoices', invoice.id] }));
    },
    onError: data => {}
  });

  const deletion = useMutation<InvoiceArticle, Error, { invoiceId: string; id: string }>({
    mutationFn: async args => {
      return await deleteInvoiceArticle(args.invoiceId, args.id, config);
    },
    onSuccess: (data, args) => {
      queryClient.invalidateQueries({ queryKey });
      queryClient.invalidateQueries({ queryKey: ['invoices', args.invoiceId] });
    },
    onError: data => {}
  });

  return { create, update, deletion } as const;
};
